import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  History as HistoryIcon,
  Swords,
  Briefcase,
  Stethoscope,
  FileText,
  Star,
  Trash2,
  ChevronDown,
  Clock,
} from "lucide-react";
import type { Attempt } from "@/core/data/types";
import { getAllAttempts, deleteAttempt } from "@/core/data/db";
import { categoryName } from "@/store/customContentStore";

type Filter = "all" | "challenge" | "interview" | "triage" | "doc";

const FILTERS: { id: Filter; label: string }[] = [
  { id: "all", label: "All" },
  { id: "challenge", label: "Challenges" },
  { id: "interview", label: "Interviews" },
  { id: "triage", label: "Triage" },
  { id: "doc", label: "Design Docs" },
];

const REF_ICON: Record<Attempt["refType"], typeof Swords> = {
  challenge: Swords,
  interview: Briefcase,
  triage: Stethoscope,
  doc: FileText,
};

function formatDate(ts: number): string {
  return new Date(ts).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export default function History() {
  const [attempts, setAttempts] = useState<Attempt[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [filter, setFilter] = useState<Filter>("all");
  const [expanded, setExpanded] = useState<number | null>(null);

  useEffect(() => {
    getAllAttempts().then((a) => {
      setAttempts(a);
      setLoaded(true);
    });
  }, []);

  const filtered = useMemo(
    () => (filter === "all" ? attempts : attempts.filter((a) => a.refType === filter)),
    [attempts, filter]
  );

  const counts = useMemo(
    () => ({
      all: attempts.length,
      challenge: attempts.filter((a) => a.refType === "challenge").length,
      interview: attempts.filter((a) => a.refType === "interview").length,
      triage: attempts.filter((a) => a.refType === "triage").length,
      doc: attempts.filter((a) => a.refType === "doc").length,
    }),
    [attempts]
  );

  async function remove(id: number) {
    await deleteAttempt(id);
    setAttempts((prev) => prev.filter((a) => a.id !== id));
  }

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-3">
          <div className="grid h-11 w-11 place-items-center rounded-xl bg-surface-2 text-primary">
            <HistoryIcon className="h-6 w-6" />
          </div>
          <div>
            <h2 className="font-display text-2xl font-semibold text-text">History</h2>
            <p className="text-sm text-muted">Every challenge and interview answer you've saved.</p>
          </div>
        </div>
      </div>

      {/* Type filter */}
      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={[
              "inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors",
              filter === f.id
                ? "border-primary/50 bg-primary/15 text-text"
                : "border-border bg-surface text-muted hover:bg-surface-2 hover:text-text",
            ].join(" ")}
          >
            {f.label}
            <span className="rounded-full bg-surface-2 px-1.5 text-xs text-muted">{counts[f.id]}</span>
          </button>
        ))}
      </div>

      {/* Empty states */}
      {loaded && attempts.length === 0 && (
        <div className="rounded-2xl border border-border bg-surface/60 p-10 text-center">
          <p className="text-muted">No saved answers yet.</p>
          <div className="mt-4 flex justify-center gap-3">
            <Link
              to="/challenge"
              className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-fg shadow-glow transition-transform hover:scale-[1.02]"
            >
              Start a challenge
            </Link>
            <Link
              to="/interview"
              className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-text hover:bg-surface-2"
            >
              Practice interviews
            </Link>
          </div>
        </div>
      )}

      {loaded && attempts.length > 0 && filtered.length === 0 && (
        <p className="text-muted">No {filter} answers yet.</p>
      )}

      {/* List */}
      <div className="space-y-3">
        {filtered.map((a) => {
          const isOpen = expanded === a.id;
          const Icon = REF_ICON[a.refType] ?? Swords;
          return (
            <motion.div
              key={a.id}
              layout
              className="overflow-hidden rounded-xl border border-border bg-surface"
            >
              <div className="flex items-start gap-3 p-4">
                <div className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-surface-2 text-accent">
                  <Icon className="h-[18px] w-[18px]" />
                </div>

                <button
                  onClick={() => setExpanded(isOpen ? null : a.id ?? null)}
                  className="min-w-0 flex-1 text-left"
                >
                  <div className="flex items-center gap-2">
                    <span className="truncate font-medium text-text">{a.title}</span>
                    {typeof a.score === "number" && (
                      <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-surface-2 px-2 py-0.5 text-xs font-medium text-muted">
                        <Star className="h-3 w-3 text-primary" />
                        {a.score}/5
                      </span>
                    )}
                  </div>
                  {a.context && <p className="mt-0.5 truncate text-sm text-muted">{a.context}</p>}
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted">
                    <span className="inline-flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {formatDate(a.createdAt)}
                    </span>
                    {a.categoryIds.slice(0, 3).map((c) => (
                      <span key={c} className="rounded-full bg-surface-2 px-2 py-0.5">
                        {categoryName(c)}
                      </span>
                    ))}
                  </div>
                </button>

                <div className="flex shrink-0 items-center gap-1">
                  <button
                    onClick={() => remove(a.id!)}
                    aria-label="Delete answer"
                    className="grid h-8 w-8 place-items-center rounded-lg text-muted transition-colors hover:bg-surface-2 hover:text-amber-400"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setExpanded(isOpen ? null : a.id ?? null)}
                    aria-label={isOpen ? "Collapse" : "Expand"}
                    className="grid h-8 w-8 place-items-center rounded-lg text-muted transition-colors hover:bg-surface-2 hover:text-text"
                  >
                    <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                  </button>
                </div>
              </div>

              {isOpen && (
                <div className="border-t border-border bg-bg/40 px-4 py-3">
                  <p className="whitespace-pre-wrap text-sm leading-relaxed text-text">
                    {a.response?.trim() || <span className="text-muted">No written response saved.</span>}
                  </p>
                </div>
              )}
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
